import { setOrganismCalm } from "../organism/calmBus";
import { clearFocusRamp, setFocusRamp } from "./nettiFocus";

// ── the reading ramp, written once, in three channels ───────────────────────
// Every netti-backed reader ramps one 0..1 value as you scroll (law_read_ramp)
// and half the chrome reads it: scrim opacity, the veil's blur radii, the corner
// frosts, the folding navigator. Writing ONE variable for all of that made every
// scroll frame do the most expensive kind of work — a new gaussian radius (the
// backdrop must be re-rasterised) AND a new max-height (the whole document below
// re-flows). That is the reader's scroll hiccup, and it was worst scrolling back
// UP, where the ramp crosses its whole range with the document fully laid out.
//
// So the ramp is written as THREE channels, quantised by what each one costs:
//
//   --art-ramp  1/100  cheap. opacity only — the compositor handles it free.
//   --art-blur  1/6    expensive. every backdrop-filter radius. ~6 re-rasters
//                      across the whole ramp instead of ~100. The fade is
//                      carried by opacity, so the radius stepping is invisible.
//   --art-fold  1/12   expensive. the only LAYOUT-affecting channel (the
//                      navigator's collapse) — ~12 reflows instead of ~100.
//
// Each channel skips its write when its own quantised value hasn't moved, so a
// trackpad firing several events per frame restyles nothing extra.

// THE BLUR IS BINARY NOW, not a ramp.
//
// Quantising the radius to 6 steps was the right instinct aimed at the wrong
// thing: a backdrop-filter's cost is not in *how often* the radius changes but
// in the fact that it changes at all. Every distinct radius throws away the
// rasterised backdrop and re-runs a gaussian over the WHOLE viewport — six
// times per scroll sweep, on a layer the size of the screen, while the document
// under it is also relaying out. That is the stutter, and it is worst scrolling
// back up where the ramp crosses its full range.
//
// So the frost has two states: off, and one constant radius. The compositor
// rasterises that blur once and reuses it for the rest of the scroll. The FADE
// is carried entirely by opacity (--*-ramp, 1/100), which is free, so nothing
// about the look is lost — the frost still arrives gradually.
//
// The switch has hysteresis so a scroll resting near the threshold can't strobe
// the layer on and off.
// ONE THRESHOLD, for everything expensive.
//
// The fold used to ride its own 1/12 channel, so the head collapsed GRADUALLY
// as you scrolled — twelve relayouts of the whole document, and a head that
// could sit stuck halfway, because halfway is a state a ramp can hold. Reading
// mode is not a spectrum. It is either on or off, and crossing into it should
// play one animation.
//
// So a single scroll point flips reading mode: the head folds (once, in CSS),
// the frost lights at one constant radius, and the organism behind it rests.
const RAMP_Q = 100;
// ⚠️ THESE ARE FRACTIONS OF EACH SURFACE'S OWN RAMP, and the pair they used to
// hold (0.06 / 0.02) is about 9px of scroll on a 220px ramp. One flick of a
// trackpad crosses a 9px band, and every crossing plays a half-second collapse
// that also moves the whole document — so the head folded, unfolded and folded
// again while you were still deciding whether to read. That is the jitter.
//
// The band no longer has to do that job ALONE, which is why it can be tighter
// than the pair the company reader was first tuned to (0.35/0.09). The rest gate
// below already cancels any crossing that reverses, so a flick can no longer
// cause a fold at all — and a wide band on top of that just meant the head hung
// open for a third of a screen after you had plainly started reading. ~58px on
// and ~16px off of a 320px ramp: it arrives when you mean it, and the gate keeps
// it honest.
const READING_ON = 0.18;
const READING_OFF = 0.05;

// ⚠️ THE HYSTERESIS BAND IS A DISTANCE, and the default one is TINY.
//
// These are fractions of a surface's own ramp, so on the company reader (whose
// ramp is 260px) the default pair is "fold at 16px of scroll, unfold at 5px" —
// an 11px dead band. One flick of a trackpad crosses it, and every crossing
// plays a 0.5s collapse that also moves the whole document. Scroll gently near
// the top and the head folds, unfolds and folds again: the jitter, and the
// "clashing" of a headline settling while the words it sits over are still
// rising past it.
//
// A surface can therefore say WHERE its reading mode begins. Pick the pair so
// that the gap between them is a real gesture (~60px+ of scroll), and so ON
// lands after the reader has genuinely committed — never in the first flick.
const FOLD_DEFAULT = { on: READING_ON, off: READING_OFF };

type Channels = { ramp: number; reading: boolean };
const last: Record<string, Channels> = {};

// ── HOLD — the ramp pinned fully in, by something other than scroll ─────────
// Editing memory (law_edit_frame) frosts the whole reader: what you are about
// to change is what you see, so the veil goes fully in and stays there no
// matter where the scroll rests. While a surface is held, scroll writes are
// remembered but not applied; on release the last one the scroll asked for is
// replayed, so the reader lands exactly where it would have been.
type Held = { wasReading: boolean; calm: boolean };
const held: Record<string, Held> = {};
type Requested = { v: number; opts?: Parameters<typeof writeReadRamp>[2] };
const requested: Record<string, Requested> = {};

// ── LET THE SCROLL REST, THEN MOVE ──────────────────────────────────────────
// A collapse that starts while the page is still travelling puts two motions on
// screen at once: the document sliding under your finger, and a head folding
// against it. They are unrelated, they run at different speeds, and together
// they read as a stutter — which is what "jittery" means here.
//
// So crossing the threshold no longer flips the fold. It ARMS it. The flip is
// committed once the scroll has been quiet for a beat, which means the head
// always plays its collapse into a still page, as one clean movement you can
// actually watch. A crossing that reverses before the beat is up (the flick that
// overshoots and comes back) is simply cancelled — that fold never happens at
// all, rather than happening twice.
//
// The beat is SHORT, and it is measured FROM THE CROSSING — not from the last
// scroll event. It only has to be long enough that a flick which immediately
// reverses can cancel itself before anything moves; past that it is just the
// head sitting open after you have plainly started reading, which reads as lag
// rather than as care.
const REST_MS = 70;
const pending: Record<string, { want: boolean; timer: ReturnType<typeof setTimeout> }> = {};

function commitReading(prefix: string, reading: boolean, calm: boolean) {
  const root = document.documentElement;
  // 0 or 1 — CSS transitions between them, so the head plays ONE clean
  // collapse instead of tracking the scroll and stalling part-way.
  root.style.setProperty(`--${prefix}-fold`, reading ? "1" : "0");
  root.toggleAttribute(`data-${prefix}-frost`, reading);
  if (calm) setOrganismCalm(`read-${prefix}`, reading ? 1 : null);
}

/** arm a fold flip, played a beat after the crossing that asked for it. */
function armReading(prefix: string, reading: boolean, calm: boolean) {
  const armed = pending[prefix];
  if (armed) {
    // ⚠️ ALREADY HEADING THERE — LET IT LAND. This used to push the rest window
    // out on every scroll event, so the beat was measured from the last scroll
    // rather than from the crossing. In one continuous gesture the events never
    // stop arriving, so the timer was cleared and re-set forever and the head
    // simply did not collapse until the page came to a complete standstill,
    // then snapped shut. That is the "unresponsive, then glitchy" head: it was
    // waiting for a stillness that a reader who is reading never gives it.
    // The beat exists to swallow a flick, and a flick is a REVERSAL — which is
    // handled below — not a long scroll.
    if (armed.want === reading) return;
    // reversed before it landed: the fold that was on its way never happens.
    clearTimeout(armed.timer);
    delete pending[prefix];
    // …and if that reversal put us back where we already are, there is nothing
    // to play at all.
    if (appliedReading(prefix) === reading) return;
  }
  pending[prefix] = {
    want: reading,
    timer: setTimeout(() => {
      delete pending[prefix];
      commitReading(prefix, reading, calm);
    }, REST_MS),
  };
}

/** the fold state actually painted right now (armed changes haven't landed). */
function appliedReading(prefix: string): boolean {
  return document.documentElement.hasAttribute(`data-${prefix}-frost`);
}

/** Write the ramp for one surface. `prefix` is the variable family ("art" or
 *  "os"), so a page-level reader and the document reader can ramp independently
 *  without one clobbering the other. */
export function writeReadRamp(
  v: number,
  prefix: "art" | "os" = "art",
  opts?: {
    /** Emit the binary reading-mode calm (default true).
     *
     *  Pass false when the caller owns the organism's rest level itself. The
     *  threshold below is a SCROLL policy: a reader's ramp starts at 0 and
     *  rises as you read, so crossing 0.06 genuinely means "reading has begun,
     *  rest the bloom". A surface that writes a STATIC frost level per view is
     *  saying something different — how veiled this view is — and a view can be
     *  barely veiled at all while the organism is the whole point of it. The
     *  deck learned this the hard way: its cover sits at exactly 0.06, tripped
     *  reading mode on the first slide, and pinned calm at 1 for the entire
     *  deck — half the backing store and a 12fps cap on the one surface where
     *  netti is the hero. */
    calm?: boolean
    /** where THIS surface's reading mode begins and ends, as fractions of its
     *  own ramp. Defaults to the house pair; widen it on any surface whose ramp
     *  is long enough that the default band is only a few pixels of scroll. */
    fold?: { on: number; off: number }
  },
): number {
  if (typeof document === "undefined") return 0;
  const c = Math.max(0, Math.min(1, v));
  const ramp = Math.round(c * RAMP_Q) / RAMP_Q;
  const fold = opts?.fold ?? FOLD_DEFAULT;
  // remembered even while held, so the release can replay the true scroll.
  requested[prefix] = { v: c, opts };
  if (held[prefix]) return 1;

  const prev = last[prefix];
  const root = document.documentElement;
  // hysteresis — hold the previous state between the two thresholds, so a
  // scroll parked on the line can't strobe reading mode on and off.
  const reading = prev ? (prev.reading ? c > fold.off : c >= fold.on) : c >= fold.on;

  if (!prev || prev.ramp !== ramp) {
    root.style.setProperty(`--${prefix}-ramp`, String(ramp));
    // …and ONE variable the band reads, whichever family wrote it.
    //
    // ⚠️ THE BAND CANNOT COMPOSE THIS ITSELF. `max(var(--art-ramp, 0),
    // var(--os-ramp, 0))` looks like the obvious CSS for "whichever surface is
    // ramping", and it is a trap: a custom property that has been SET TO EMPTY
    // is a valid value, so the fallback never applies, the `max()` receives a
    // blank operand, the whole calc is invalid at computed-value time, and the
    // opacity silently falls back to 1 — a band at full strength on a page that
    // has not been scrolled. One variable, written where the ramp is known.
    root.style.setProperty("--band-ramp", String(ramp));
    // …and ONE the reading frost over netti reads (OrganismBackdrop's
    // .ob-frost), for the same reason. The band's is also written by
    // writeBandRamp; this one only ever by a reading ramp.
    root.style.setProperty("--read-ramp", String(ramp));
    // …and netti's focus pull (lib/os/nettiFocus), the same ramp read as how
    // far out of focus the creature is. Uniform, never a frost over it.
    setFocusRamp(prefix, ramp);
  }

  // The fold is ARMED here and committed once the scroll rests (see above), so
  // the collapse always plays into a still page. The organism's rest goes with
  // it: nothing in the reader used to tell the bloom to calm down — only modals
  // emitted calm — so it kept rendering at full power behind the frost,
  // repainting the very backdrop the scrim has to blur. A backdrop-filter only
  // re-runs when its backdrop repaints, so an animating organism was silently
  // paying for the frost on every frame.
  const calm = opts?.calm !== false;
  const armed = pending[prefix];
  if (armed) {
    // a crossing that reverses before the beat is up never happens at all
    if (armed.want !== reading) {
      clearTimeout(armed.timer);
      delete pending[prefix];
      if (reading !== appliedReading(prefix)) armReading(prefix, reading, calm);
    } else {
      armReading(prefix, reading, calm);
    }
  } else if (reading !== appliedReading(prefix)) {
    armReading(prefix, reading, calm);
  }

  last[prefix] = { ramp, reading };
  return ramp;
}

/** The navigator BAND alone. An age-scrub view (the activity river, a project
 *  timeline) keeps netti sharp and unrested — it writes the ramp as 0 — but its
 *  rows still scroll under the crumb and the head, and the band is what hides
 *  them. It rode the ramp, so on those views nothing did: rows drew straight
 *  through the header. The band follows the real scroll here, frost or not. */
export function writeBandRamp(v: number): void {
  if (typeof document === "undefined") return;
  const ramp = Math.round(Math.max(0, Math.min(1, v)) * RAMP_Q) / RAMP_Q;
  document.documentElement.style.setProperty("--band-ramp", String(ramp));
}

/** Reset every channel — on unmount, or on a route change, so the next surface
 *  opens fully lit. Clears the memo so the first write always lands. */
export function clearReadRamp(prefix: "art" | "os" = "art", remove = true) {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  // a fold armed on the way out must never land on the next surface
  const armed = pending[prefix];
  if (armed) { clearTimeout(armed.timer); delete pending[prefix]; }
  for (const ch of ["ramp", "blur", "fold"] as const) {
    if (remove) root.style.removeProperty(`--${prefix}-${ch}`);
    else root.style.setProperty(`--${prefix}-${ch}`, "0");
  }
  // the band goes out with it — a surface that unmounts mid-scroll must not
  // leave the next one opening behind a lit band.
  if (remove) root.style.removeProperty("--band-ramp");
  else root.style.setProperty("--band-ramp", "0");
  if (remove) root.style.removeProperty("--read-ramp");
  else root.style.setProperty("--read-ramp", "0");
  clearFocusRamp(prefix);
  // the frost must go out with the ramp, or the next surface opens behind a
  // full-viewport gaussian nobody asked for — and the organism must be released,
  // or it stays rested on a page with no frost over it at all.
  root.removeAttribute(`data-${prefix}-frost`);
  setOrganismCalm(`read-${prefix}`, null);
  setOrganismCalm(`hold-${prefix}`, null);
  delete held[prefix];
  delete requested[prefix];
  delete last[prefix];
}

/** Pin one surface's ramp fully in (opacity 1, the constant frost radius lit,
 *  the organism rested) regardless of scroll — or release it. The navigator's
 *  fold is deliberately left alone: the hold is about the veil, not about
 *  collapsing chrome under the reader's hands. Releasing restores the frost
 *  state the scroll had earned and replays the latest scroll value, so the
 *  ramp continues from where the page actually is. Idempotent either way. */
export function holdReadRamp(on: boolean, prefix: "art" | "os" = "art", opts?: { calm?: boolean }) {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  const calm = opts?.calm !== false;
  if (on) {
    if (held[prefix]) return;
    // a fold flip armed before the hold must not land underneath it
    const armed = pending[prefix];
    if (armed) { clearTimeout(armed.timer); delete pending[prefix]; }
    held[prefix] = { wasReading: appliedReading(prefix), calm };
    root.style.setProperty(`--${prefix}-ramp`, "1");
    root.style.setProperty("--read-ramp", "1");
    setFocusRamp(prefix, 1);
    root.setAttribute(`data-${prefix}-frost`, "");
    if (calm) setOrganismCalm(`hold-${prefix}`, 1);
    return;
  }
  const h = held[prefix];
  if (!h) return;
  delete held[prefix];
  setOrganismCalm(`hold-${prefix}`, null);
  // back to what scroll had earned, then let the real scroll value settle it
  root.toggleAttribute(`data-${prefix}-frost`, h.wasReading);
  delete last[prefix];
  const req = requested[prefix];
  writeReadRamp(req?.v ?? 0, prefix, req?.opts);
}
